import React, {useEffect, useMemo, useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {Dropdown, DropDownItem} from "../common/DropDown";
import {BookmakerData, EventSettingsObject, EventsStoredObjects, User,} from "../../../@types/response";
import {useDispatch, useSelector} from "react-redux";
import {getBookmakers} from "../../../store/selectors/getBookmakers";
import FilterInput from "../common/FilterInput";
import {getErrorMessage} from "../../../store/selectors/getErrorMessage";
import {Autocomplete} from "../common/Autocomplete";
import {getFixtures} from "../../../store/selectors/getFixtures";
import {searchActiveFixtures} from "../../../store/utils/searchFixtures";
import {faTrophy} from "@fortawesome/free-solid-svg-icons/faTrophy";
import {Drawer} from "../common/Drawer";
import {faRetweet} from "@fortawesome/free-solid-svg-icons";
import Logo from "../logo";
import Separator from "../Separator";
import {SetSettingsAction} from "../../../store/actions/event/SetSettingsAction";
import {GetEventsAction} from "../../../store/actions/event/GetEventsAction";
import {getSettings} from "../../../store/selectors/event/getSettings";
import {isInitialized} from "../../../store/selectors/event/isInitialized";
import {HeartBeatDropDown} from "../common/HeartBeatDropDown";
import getClient from "../../../store/selectors/getClient";
import FilterNameInput from "../common/FilterNameInput";
import {ToolBarEventsProps} from "../../../@types/inputs";
import {AppDispatch} from "recharts/types/state/store";

export default function Toolbar({search, setSearch}: ToolBarEventsProps) {
    const allBookmakers: BookmakerData = useSelector(getBookmakers);

    const fixtures: EventsStoredObjects = useSelector(getFixtures);
    const errorMessage: string = useSelector(getErrorMessage);
    const storedSettings: EventSettingsObject = useSelector(getSettings);
    const loading: boolean = useSelector(isInitialized);
    const client: User | undefined = useSelector(getClient);

    const [pendingSettings, setPendingSettings] = useState<boolean>(false);
    const [showTournament, setShowTournament] = useState<boolean>(false);
    const [settings, setSetting] = useState<EventSettingsObject>(storedSettings);
    const [searchQuery, setSearchQuery] = useState<string>('');

    const suggestions = useMemo(
            () => searchActiveFixtures(fixtures, null, searchQuery),
            [fixtures, searchQuery]
    );

    const dispatch = useDispatch<AppDispatch>();

    const bookmakers: BookmakerData = useMemo(() => {
        return Object.keys(allBookmakers)
                .filter(id => client?.client == null || client.client === parseInt(id))
                .reduce((a, v) => ({...a, [v]: allBookmakers[v]}), {})
    }, [allBookmakers])

    useEffect(() => {
        setSetting(storedSettings);
    }, [storedSettings]);

    useEffect(() => {
        setPendingSettings(JSON.stringify(settings) !== JSON.stringify(storedSettings));
    }, [settings, storedSettings]);

    return (
            <div className="flex w-full h-14 2xl:text-monitorBase text-monitorSm">
                <Drawer
                        settings={settings}
                        isOpen={showTournament}
                        onClose={() => null}
                        onChange={(sports) => setSetting(Object.assign({}, settings, {sports}))}
                />
                <Logo/>
                <ul className="flex items-center justify-around py-2 w-full">
                    <Separator/>
                    <li className="mx-4">
                        <Autocomplete
                                placeholder="search event"
                                searchAction={setSearchQuery}
                                suggestions={suggestions}
                        />
                    </li>
                    <Separator/>
                    <li className="mx-4">
                        <div className="relative flex flex-col ml-4">
                            <label htmlFor="dataFilter" className={"text-white"}>
                                DATE
                            </label>
                            <select
                                    id="dataFilter"
                                    name="dataFilter"
                                    className={"text-white bg-toolbar-bgMain"}
                                    onChange={({target}) =>
                                            setSetting(Object.assign({}, settings, {date: parseInt(target.value)}))
                                    }
                                    value={settings.date}
                            >
                                <option value={0}>All</option>
                                <option value={1}>Today</option>
                                <option value={2}>Today + 1</option>
                                <option value={3}>Today + 2</option>
                                <option value={4}>Today + 3</option>
                                <option value={5}>Today + 4</option>
                                <option value={6}>Today + 5</option>
                                <option value={7}>Today + 6</option>
                            </select>
                        </div>
                    </li>
                    <Separator/>
                    <li className="mx-4">
                        <FilterInput
                                id="from"
                                name="from"
                                label="FROM"
                                min={1}
                                max={50}
                                step={0.1}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    if (isNaN(parseInt(e.target.value))) return;
                                    setSetting(Object.assign({}, settings, {from: parseFloat(e.target.value)}));
                                }}
                                value={settings.from}
                        />
                    </li>
                    <Separator/>
                    <li className="mx-4">
                        <FilterInput
                                id="to"
                                name="to"
                                label="TO"
                                min={1}
                                max={100}
                                step={0.1}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    if (isNaN(parseInt(e.target.value))) return;
                                    setSetting(Object.assign({}, settings, {to: parseFloat(e.target.value)}));
                                }}
                                value={settings.to}
                        />
                    </li>
                    <Separator/>
                    <li className="flex flex-col items-center">
                        <Dropdown label="BOOKMAKER" color="default">
                            {Object.values(bookmakers)
                                    .sort((a, b) => (b.name > a.name ? -1 : 1))
                                    .map((bookmaker) => {
                                        if (bookmaker.isComparison) return null;
                                        const visible = settings.bookmakers.includes(bookmaker.id);
                                        return (
                                                <DropDownItem
                                                        key={bookmaker.name}
                                                        label={bookmaker.name}
                                                        selected={visible}
                                                        onClick={() => {
                                                            if (!visible) {
                                                                setSetting(
                                                                        Object.assign({}, settings, {
                                                                            bookmakers: [bookmaker.id],
                                                                            bookmaker: bookmaker.id,
                                                                        })
                                                                );
                                                            } else {
                                                                setSetting(
                                                                        Object.assign({}, settings, {
                                                                            bookmakers: [],
                                                                            bookmaker: null
                                                                        })
                                                                );
                                                            }
                                                        }}
                                                />
                                        );
                                    })}
                        </Dropdown>
                    </li>
                    <Separator/>

                    <li>
                        <button
                                className={`p-3 rounded-md ${
                                        loading
                                                ? "bg-toolbar-settingsLoading text-white "
                                                : pendingSettings
                                                        ? "bg-toolbar-settingsWarning text-black "
                                                        : "bg-toolbar-settings text-white "
                                }`}
                                onClick={() => {
                                    dispatch(SetSettingsAction(settings));
                                    settings.bookmaker && dispatch(GetEventsAction(settings.bookmaker));
                                }}
                        >
                            <FontAwesomeIcon className={loading ? "animate-spin" : ""} icon={faRetweet}/>
                        </button>
                    </li>
                    <li className="mx-4">
                        <button
                                className={"peer h-10 bg-toolbar-bgMain text-white mx-3"}
                                onClick={() => setShowTournament(!showTournament)}
                        >
                            <FontAwesomeIcon icon={faTrophy}/>
                        </button>
                    </li>
                    <li className="mr-4">
                        <HeartBeatDropDown/>
                    </li>
                    <Separator/>
                    <li className="mx-4  flex items-center ">
                        <FilterNameInput
                                id="player-search"
                                placeholder="search ..."
                                setValue={setSearch}
                                value={search}
                        />
                    </li>
                </ul>
                <div>{errorMessage}</div>
            </div>
    );
}
